import { useApolloClient } from "@apollo/client";
import { useEffect, useRef } from "react";
import { NOTIFICATION_SUBSCRIPTION } from "../GraphQLModules/Notifications/queries";

const RECONNECT_DELAY = 2000;

const useNotificationSubscription = ({ tenantId, enabled, onNotification }) => {
  const client = useApolloClient();
  const subscriptionRef = useRef(null);
  const reconnectTimerRef = useRef(null);

  useEffect(() => {
    let isActive = true;

    const cleanupSubscription = () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
        subscriptionRef.current = null;
      }
    };

    const scheduleReconnect = () => {
      if (!isActive) {
        return;
      }
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = setTimeout(() => {
        startSubscription();
      }, RECONNECT_DELAY);
    };

    const startSubscription = () => {
      if (!isActive || !enabled || !tenantId) {
        return;
      }

      cleanupSubscription();

      const observable = client.subscribe({
        query: NOTIFICATION_SUBSCRIPTION,
        variables: {
          tenant_id: tenantId,
        },
        fetchPolicy: "no-cache",
      });

      subscriptionRef.current = observable.subscribe({
        next: ({ data }) => {
          const notification = data?.notificationReceived;
          if (!notification) return;
          console.log("Notification received via subscription", notification);
          onNotification?.(notification);
        },
        error: (error) => {
          console.error("Notification subscription error", error);
          scheduleReconnect();
        },
        complete: () => {
          scheduleReconnect();
        },
      });
    };

    if (enabled && tenantId) {
      startSubscription();
    }

    return () => {
      isActive = false;
      clearTimeout(reconnectTimerRef.current);
      cleanupSubscription();
    };
  }, [client, enabled, tenantId, onNotification]);
};

export default useNotificationSubscription;
